/**
 * TOSS Web Application — Select
 *
 * Поле выбора одного значения из списка.
 *
 * ---------------------------------------------------------------------------
 * ПОЧЕМУ СВОЙ СПИСОК, А НЕ СИСТЕМНЫЙ (27.08.2026)
 * ---------------------------------------------------------------------------
 *
 * До этого дня здесь был голый `<select>` со стрелкой из `background-image`. Закрытым он выглядел
 * как поле; открытым — как чужая программа: на телефоне поверх формы выезжал системный список ОС,
 * на компьютере — список браузера, и оба игнорируют и палитру, и шрифт, и высоту контролов.
 * Владелец, увидев это на форме сотрудника: «выбор роли через дропдаун сделать просто, починить
 * его, это общий виджет — его нужно подлечить, чтобы по всему проекту дропдауны хорошо работали».
 *
 * Поэтому: триггер — кнопка ростом с поле, список — свой слой шириной ровно в поле, у выбранного
 * значения галочка. **Одно поведение на обеих поверхностях** (решение владельца): список
 * раскрывается ПОД полем и на компьютере, и на телефоне, а не листом снизу — один код на весь
 * проект, и в админке он ведёт себя так же, как в кабинете.
 *
 * ---------------------------------------------------------------------------
 * НАТИВНЫЙ `<select>` НИКУДА НЕ ДЕЛСЯ, И ЭТО НЕ ПЕРЕСТРАХОВКА
 * ---------------------------------------------------------------------------
 *
 * Он остаётся в разметке скрытым и продолжает быть ИСТОЧНИКОМ ЗНАЧЕНИЯ. На нём висят `ref`, `name`
 * и всё, что вызывающая сторона передаёт спредом, поэтому 45 существующих мест не заметили
 * переделки: `onChange(e)` по-прежнему приходит с `e.target.value`, а `{...register('field')}`
 * из react-hook-form по-прежнему регистрирует настоящий элемент формы.
 *
 * Выбор из своего списка пишется в него через НАТИВНЫЙ сеттер значения и настоящее событие
 * `change`. React слушает события на корне документа и подменяет `value` у элемента своим
 * дескриптором — присваивание `el.value = …` он бы не заметил, и подписчик не сработал бы.
 *
 * `required` на скрытый элемент НЕ уходит (он деструктурирован ради звёздочки в подписи): браузер
 * отказывается показывать сообщение о пустом обязательном поле, которое не может сфокусировать, и
 * отправка формы падала бы с «An invalid form control … is not focusable». Обязательность
 * проверяют схемы валидации, как и у остальных полей проекта.
 */

import {
  Children,
  forwardRef,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';
import { Check, ChevronDown } from 'lucide-react';
import {
  baseInputStyles,
  fieldHeightStyles,
  inputSizeStyles,
  inputStateStyles,
  type BaseInputProps,
} from './fieldStyles';
import { FieldLabel } from './FieldLabel';
import { DropdownPortal } from '../DropdownPortal';
import { SearchInput } from '../SearchInput';

// ============================================================================
// Types
// ============================================================================

export interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
  /** Вторая строка под названием — для списков, где название само за себя не говорит (роли). */
  description?: string;
}

export interface SelectProps
  extends SelectHTMLAttributes<HTMLSelectElement>,
    Omit<BaseInputProps, 'leftIcon' | 'rightIcon'> {
  /** Placeholder option text */
  placeholder?: string;
  /** Select options */
  options?: SelectOption[];
  /**
   * Строка поиска НАД списком — для справочников, которые глазами уже не пролистываются.
   *
   * Заведено 31.08.2026 вместо модального выбора направления: справочник из одного значения
   * открывался окном во весь экран с поиском и подтверждением, то есть пятью действиями там,
   * где хватает трёх. Множественный выбор — контейнеры, установки, пункты — окном и остаётся:
   * там сотни строк, страницы по 50 и «выбрать всё», и список под полем этого не выдержит.
   */
  searchable?: boolean;
  /** Подпись строки поиска. */
  searchPlaceholder?: string;
  /** Что печатать, когда поиск ничего не нашёл. */
  emptyMessage?: string;
}

// ============================================================================
// Component
// ============================================================================

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      error,
      helperText,
      required,
      inputSize = 'md',
      fullWidth = true,
      wrapperClassName = '',
      className = '',
      id: providedId,
      placeholder,
      options,
      searchable = false,
      searchPlaceholder = 'Поиск',
      emptyMessage = 'Ничего не найдено',
      grayBackground = false,
      children,
      disabled,
      value,
      defaultValue,
      onChange,
      // Подпись описывает ТРИГГЕР — его читает и видит пользователь. На скрытом `<select>`
      // она осталась бы у элемента, которого нет на экране.
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const selectId = providedId ?? generatedId;
    const errorId = `${selectId}-error`;
    const helperId = `${selectId}-helper`;
    const listId = `${selectId}-list`;

    const hasError = !!error;

    const triggerRef = useRef<HTMLButtonElement>(null);
    const selectRef = useRef<HTMLSelectElement | null>(null);

    const searchRef = useRef<HTMLInputElement>(null);

    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [query, setQuery] = useState('');

    /**
     * Опции могут приехать массивом или `<option>`-детьми — второе осталось от нативного времени и
     * живёт в двух местах админского журнала аудита. `Children.forEach` разворачивает и массивы, и
     * фрагменты, так что `.map()` в JSX читается так же, как готовый список.
     */
    const items = useMemo<SelectOption[]>(() => {
      if (options && options.length > 0) return options;

      const collected: SelectOption[] = [];
      Children.forEach(children, (child) => {
        if (!isValidElement(child) || child.type !== 'option') return;
        const optionProps = child.props as {
          value?: string | number;
          children?: ReactNode;
          disabled?: boolean;
        };
        collected.push({
          value: optionProps.value ?? '',
          label: typeof optionProps.children === 'string' ? optionProps.children : String(optionProps.children ?? ''),
          disabled: optionProps.disabled,
        });
      });
      return collected;
    }, [options, children]);

    // Значение живёт в скрытом `<select>`; здесь его копия — для того, что рисуется. Управляемый
    // вызов присылает `value`, неуправляемый (через `register`) не присылает ничего, и тогда
    // стартовое значение берётся из `defaultValue` или из первой опции, как поступил бы браузер.
    const [selected, setSelected] = useState<string>(() => {
      if (value !== undefined && value !== null) return String(value);
      if (defaultValue !== undefined && defaultValue !== null) return String(defaultValue);
      return placeholder ? '' : String(items[0]?.value ?? '');
    });

    useEffect(() => {
      if (value === undefined || value === null) return;
      setSelected(String(value));
    }, [value]);

    const selectedOption = items.find((option) => String(option.value) === selected);

    /**
     * То, что видно в списке СЕЙЧАС. Всё, что считает индексы — активная строка, стрелки, Enter, —
     * считает их по этому массиву, а не по `items`: иначе первая же напечатанная буква сдвинула бы
     * выбор на строку, которой на экране нет. Скрытый `<select>` при этом держит ВСЕ опции — он
     * источник значения, а не список для глаз.
     */
    const visible = useMemo<SelectOption[]>(() => {
      const needle = query.trim().toLowerCase();
      if (!searchable || !needle) return items;
      return items.filter(
        (option) =>
          option.label.toLowerCase().includes(needle) ||
          (option.description ?? '').toLowerCase().includes(needle)
      );
    }, [items, query, searchable]);

    const commit = useCallback((next: string): void => {
      const element = selectRef.current;
      if (element) {
        // Именно нативный сеттер: React подменяет `value` собственным дескриптором и присваивание
        // «мимо» него не породило бы события, на которое подписаны `onChange` и `register`.
        const setter = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value')?.set;
        setter?.call(element, next);
        element.dispatchEvent(new Event('change', { bubbles: true }));
      }
      setSelected(next);
    }, []);

    const open = useCallback((): void => {
      if (disabled) return;
      // Поиск открывается пустым: прошлый запрос — это прошлый разговор, и список, открывшийся
      // отфильтрованным, читается как «справочник опустел».
      setQuery('');
      const current = items.findIndex((option) => String(option.value) === selected);
      setActiveIndex(current >= 0 ? current : 0);
      setIsOpen(true);
    }, [disabled, items, selected]);

    const close = useCallback((): void => {
      setIsOpen(false);
      setQuery('');
    }, []);

    // Фокус уезжает в поиск сам: иначе открывший список с клавиатуры печатал бы в никуда.
    useEffect(() => {
      if (!isOpen || !searchable) return;
      searchRef.current?.focus();
    }, [isOpen, searchable]);

    /** Следующая ВЫБИРАЕМАЯ опция в заданную сторону — отключённые перешагиваются. */
    const step = useCallback(
      (from: number, direction: 1 | -1): number => {
        if (visible.length === 0) return from;
        let next = from;
        for (let i = 0; i < visible.length; i += 1) {
          next = (next + direction + visible.length) % visible.length;
          if (!visible[next]?.disabled) return next;
        }
        return from;
      },
      [visible]
    );

    const handleKeyDown = (event: KeyboardEvent<HTMLElement>): void => {
      if (!isOpen) {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          open();
        }
        return;
      }

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setActiveIndex((index) => step(index, 1));
          break;
        case 'ArrowUp':
          event.preventDefault();
          setActiveIndex((index) => step(index, -1));
          break;
        case 'Home':
          event.preventDefault();
          setActiveIndex(step(visible.length - 1, 1));
          break;
        case 'End':
          event.preventDefault();
          setActiveIndex(step(0, -1));
          break;
        case 'Enter': {
          event.preventDefault();
          const option = visible[activeIndex];
          if (option && !option.disabled) commit(String(option.value));
          close();
          triggerRef.current?.focus();
          break;
        }
        case ' ': {
          // Пробел выбирает только там, где он не может быть частью запроса: в строке поиска это
          // обычный символ, и «Москва Казань» иначе не набрать.
          if (searchable) break;
          event.preventDefault();
          const option = visible[activeIndex];
          if (option && !option.disabled) commit(String(option.value));
          close();
          break;
        }
        case 'Tab':
          close();
          break;
        default:
          break;
      }
    };

    /* Тихий фон вместо рамки — тот же рецепт, что у полей формы заявки: десять обведённых
       прямоугольников подряд это десять границ, которые глаз обязан пересечь. Рамка приходит на
       наведении, фокусе и когда список раскрыт. */
    const grayTrigger = hasError
      ? 'bg-[var(--color-bg-secondary)] border-[var(--color-error)]'
      : 'bg-[var(--color-bg-secondary)] border-transparent hover:border-[var(--color-border)] focus-visible:border-[var(--color-border-focus)]';

    const triggerClasses = [
      baseInputStyles,
      inputSizeStyles[inputSize],
      fieldHeightStyles[inputSize],
      grayBackground ? grayTrigger : hasError ? inputStateStyles.error : inputStateStyles.default,
      'flex items-center gap-2 text-left',
      isOpen ? 'border-[var(--color-accent-500)]' : '',
      disabled ? 'cursor-not-allowed' : 'cursor-pointer',
      className,
    ]
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();

    // `w-full` объявлен в Tailwind ПОЗЖЕ числовых ширин, поэтому ширина, пришедшая в
    // `wrapperClassName`, ему проигрывает: задавать её можно только вместе с `fullWidth={false}`.
    const wrapperClasses = [fullWidth ? 'w-full' : '', wrapperClassName].join(' ');

    return (
      <div className={wrapperClasses}>
        <FieldLabel htmlFor={selectId} label={label} required={required} />

        <button
          ref={triggerRef}
          type="button"
          id={selectId}
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-controls={isOpen ? listId : undefined}
          aria-activedescendant={isOpen && !searchable ? `${listId}-${activeIndex}` : undefined}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          aria-invalid={hasError}
          aria-describedby={hasError ? errorId : helperText ? helperId : undefined}
          disabled={disabled}
          onClick={() => (isOpen ? close() : open())}
          onKeyDown={handleKeyDown}
          className={triggerClasses}
        >
          <span
            className={`min-w-0 flex-1 truncate ${
              selectedOption ? '' : 'text-[var(--color-text-disabled)]'
            }`}
          >
            {selectedOption?.label ?? placeholder ?? ''}
          </span>
          <ChevronDown
            className={`h-4 w-4 flex-shrink-0 text-[var(--color-text-secondary)] transition-transform ${
              isOpen ? 'rotate-180' : ''
            }`}
            aria-hidden="true"
          />
        </button>

        <DropdownPortal
          isOpen={isOpen}
          onClose={close}
          triggerRef={triggerRef}
          role="listbox"
          id={listId}
          matchTriggerWidth
          // Сторону и высоту считает слой: внутри листа снизу места нет почти никогда, а
          // «открыться вверх» без ограничения высоты означало накрыть собой поля формы.
          autoFit
          className="overflow-y-auto rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-bg-card)] py-1 shadow-[var(--shadow-overlay)]"
        >
          {searchable && (
            <div className="p-2 pb-1">
              <SearchInput
                ref={searchRef}
                value={query}
                onChange={(next) => {
                  setQuery(next);
                  // Активная строка возвращается на первую: после фильтра прежний индекс указывал
                  // бы на строку, которой в списке уже нет.
                  setActiveIndex(0);
                }}
                onKeyDown={handleKeyDown}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                aria-controls={listId}
                aria-activedescendant={`${listId}-${activeIndex}`}
              />
            </div>
          )}

          {visible.length === 0 && (
            <p className="px-3 py-3 text-md text-[var(--color-text-secondary)]">{emptyMessage}</p>
          )}

          {visible.map((option, index) => {
            const isSelected = String(option.value) === selected;
            const isActive = index === activeIndex;

            return (
              <div
                key={option.value}
                id={`${listId}-${index}`}
                role="option"
                aria-selected={isSelected}
                aria-disabled={option.disabled}
                // Мышь ведёт активную строку за собой, чтобы клавиатура и курсор не спорили о том,
                // какая строка «текущая».
                onMouseEnter={() => !option.disabled && setActiveIndex(index)}
                onClick={() => {
                  if (option.disabled) return;
                  commit(String(option.value));
                  close();
                  triggerRef.current?.focus();
                }}
                className={`flex min-h-[var(--h-control)] cursor-pointer items-center gap-2 px-3 py-2 text-md ${
                  option.disabled
                    ? 'cursor-not-allowed text-[var(--color-text-disabled)]'
                    : isActive
                      ? 'bg-[var(--color-bg-secondary)] text-[var(--color-text-primary)]'
                      : 'text-[var(--color-text-primary)]'
                }`}
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{option.label}</span>
                  {option.description && (
                    <span className="block text-xs text-[var(--color-text-secondary)]">
                      {option.description}
                    </span>
                  )}
                </span>
                {isSelected && (
                  <Check className="h-4 w-4 flex-shrink-0 text-[var(--color-primary-600)]" aria-hidden="true" />
                )}
              </div>
            );
          })}
        </DropdownPortal>

        {/* Источник значения для формы: `ref`, `name` и спред остаются на нём. Скрыт от глаз и от
            табуляции, но остаётся в DOM — иначе `register()` регистрировал бы пустоту. */}
        <select
          ref={(node) => {
            selectRef.current = node;
            if (typeof ref === 'function') ref(node);
            else if (ref) (ref as React.MutableRefObject<HTMLSelectElement | null>).current = node;
          }}
          value={selected}
          onChange={(event) => {
            setSelected(event.target.value);
            onChange?.(event);
          }}
          disabled={disabled}
          tabIndex={-1}
          aria-hidden="true"
          className="sr-only"
          {...props}
        >
          {placeholder && <option value="">{placeholder}</option>}
          {items.map((option) => (
            <option key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </option>
          ))}
        </select>

        {hasError && typeof error === 'string' && (
          <p id={errorId} className="mt-1 text-sm text-[var(--color-error)]">
            {error}
          </p>
        )}

        {helperText && !hasError && (
          <p id={helperId} className="mt-1 text-sm text-[var(--color-text-secondary)]">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';

export default Select;
